import { World, Bodies, type Body } from "fast-2d";

/** Standard world: gravity pulling +y (down on screen). */
export function makeWorld(): World {
	return new World({ gravity: [0, 9.81] });
}

interface BoxOpts {
	angle?: number;
	density?: number;
	restitution?: number;
	friction?: number;
	isStatic?: boolean;
	linearDamping?: number;
	angularDamping?: number;
	velocity?: [number, number];
	angularVelocity?: number;
}

export function box(
	world: World,
	x: number,
	y: number,
	w: number,
	h: number,
	opts: BoxOpts = {},
): Body {
	return world.add(Bodies.rectangle({ position: [x, y], width: w, height: h, ...opts }));
}

export function ball(
	world: World,
	x: number,
	y: number,
	r: number,
	opts: BoxOpts = {},
): Body {
	return world.add(Bodies.circle({ position: [x, y], radius: r, ...opts }));
}

/** A convex polygon body from local-space vertices (centered on `x, y`). */
export function polygon(
	world: World,
	x: number,
	y: number,
	vertices: [number, number][],
	opts: BoxOpts = {},
): Body {
	return world.add(Bodies.polygon({ position: [x, y], vertices, ...opts }));
}

/**
 * A regular `sides`-gon (triangle, pentagon, hexagon, …) of the given
 * circumradius, with a flat-ish base so it stacks nicely on the ground.
 */
export function nGon(
	world: World,
	x: number,
	y: number,
	sides: number,
	radius: number,
	opts: BoxOpts = {},
): Body {
	const verts: [number, number][] = [];
	// Start near the bottom so even-sided shapes rest on an edge, not a vertex.
	const base = Math.PI / 2 + Math.PI / sides;
	for (let i = 0; i < sides; i++) {
		const a = base + (i / sides) * Math.PI * 2;
		verts.push([Math.cos(a) * radius, Math.sin(a) * radius]);
	}
	return polygon(world, x, y, verts, opts);
}

/**
 * A symmetric trapezoid (wider base at the bottom). `slope` in `[0, 1)` sets how
 * much the top edge narrows. Vertices are recentered on the centroid so the body
 * spins about its true center of mass.
 */
export function trapezoid(
	world: World,
	x: number,
	y: number,
	width: number,
	height: number,
	slope: number,
	opts: BoxOpts = {},
): Body {
	const hw = width / 2;
	const hh = height / 2;
	const tw = hw * (1 - slope);
	const verts: [number, number][] = [
		[-hw, hh],
		[hw, hh],
		[tw, -hh],
		[-tw, -hh],
	];
	// Shift to the centroid so rotation looks natural.
	let area = 0;
	let cx = 0;
	let cy = 0;
	for (let i = 0; i < verts.length; i++) {
		const [ax, ay] = verts[i]!;
		const [bx, by] = verts[(i + 1) % verts.length]!;
		const cross = ax * by - bx * ay;
		area += cross;
		cx += (ax + bx) * cross;
		cy += (ay + by) * cross;
	}
	area *= 0.5;
	cx /= 6 * area;
	cy /= 6 * area;
	const centered = verts.map(([vx, vy]) => [vx - cx, vy - cy] as [number, number]);
	return polygon(world, x, y, centered, opts);
}

/** A static floor whose top surface sits at world y = 0. */
export function floor(world: World, width = 40, thickness = 1): Body {
	return box(world, 0, thickness / 2, width, thickness, { isStatic: true });
}

/** Two static side walls forming a bin of the given inner half-width. */
export function walls(world: World, halfWidth: number, height: number, thickness = 1): void {
	box(world, -halfWidth - thickness / 2, -height / 2, thickness, height, { isStatic: true });
	box(world, halfWidth + thickness / 2, -height / 2, thickness, height, { isStatic: true });
}

/** Tiny, fast, deterministic PRNG (mulberry32) — keeps demos reproducible. */
export function rng(seed: number): () => number {
	let a = seed >>> 0;
	return () => {
		a = (a + 0x6d2b79f5) | 0;
		let t = Math.imul(a ^ (a >>> 15), 1 | a);
		t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
		return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
	};
}
